import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Phone,
  CalendarCheck,
  ArrowUp,
  InstagramLogo,
  TiktokLogo,
  AmazonLogo,
  ShareNetwork,
  WhatsappLogo,
  X,
} from "@phosphor-icons/react";
import { useLang } from "@/context/LanguageContext";
import { contacts } from "@/data/content";
import { scrollToId } from "@/components/Reveal";

const socials = [
  { key: "instagram", label: "Instagram", href: contacts.social.instagram, Icon: InstagramLogo },
  { key: "tiktok", label: "TikTok", href: contacts.social.tiktok, Icon: TiktokLogo },
  { key: "amazon", label: "Amazon", href: contacts.social.amazon, Icon: AmazonLogo },
];

export const FloatingActions = () => {
  const { t, lang } = useLang();
  const [show, setShow] = useState(false);
  const [socialsOpen, setSocialsOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 600);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const waHref = `https://wa.me/${contacts.whatsappNumber}?text=${encodeURIComponent(
    contacts.whatsappMessage[lang] || contacts.whatsappMessage.it
  )}`;

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          data-testid="floating-actions"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 30 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="fixed bottom-24 right-6 z-50 flex flex-col items-end gap-3"
        >
          <button
            data-testid="scroll-top"
            onClick={() => scrollToId("hero")}
            aria-label="Top"
            className="h-11 w-11 bg-white/80 backdrop-blur-md border border-black/10 text-[#1A1A1A] flex items-center justify-center hover:bg-[#1A1A1A] hover:text-white transition-colors shadow-lg"
          >
            <ArrowUp size={18} />
          </button>

          <div className="relative flex flex-col items-end gap-2">
            <AnimatePresence>
              {socialsOpen &&
                socials.map((s, i) => (
                  <motion.a
                    key={s.key}
                    data-testid={`floating-social-${s.key}`}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    title={s.label}
                    initial={{ opacity: 0, y: 8, scale: 0.9 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 8, scale: 0.9 }}
                    transition={{
                      duration: 0.3,
                      delay: i * 0.05,
                      ease: [0.16, 1, 0.3, 1],
                    }}
                    className="h-11 w-11 bg-white/90 backdrop-blur-md border border-black/10 text-[#1A1A1A] flex items-center justify-center hover:bg-[#8B7355] hover:text-white hover:border-[#8B7355] transition-colors shadow-lg"
                  >
                    <s.Icon size={18} weight="light" />
                  </motion.a>
                ))}
            </AnimatePresence>

            <button
              data-testid="floating-social-toggle"
              onClick={() => setSocialsOpen((o) => !o)}
              aria-label="Social"
              aria-expanded={socialsOpen}
              className="h-11 w-11 bg-white/80 backdrop-blur-md border border-black/10 text-[#1A1A1A] flex items-center justify-center hover:bg-[#1A1A1A] hover:text-white transition-colors shadow-lg"
            >
              {socialsOpen ? <X size={18} /> : <ShareNetwork size={18} />}
            </button>
          </div>

          <button
            data-testid="floating-book"
            onClick={() => scrollToId("contact")}
            className="group inline-flex items-center gap-2 bg-[#1A1A1A] text-[#FAFAF7] pl-5 pr-6 py-3.5 text-sm shadow-xl hover:bg-[#8B7355] transition-colors"
          >
            <CalendarCheck size={18} weight="light" />
            <span className="hidden sm:inline">{t.floating.book}</span>
          </button>

          <a
            data-testid="floating-whatsapp"
            href={waHref}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2 bg-[#25D366] text-white pl-5 pr-6 py-3.5 text-sm shadow-xl hover:bg-[#128C7E] transition-colors"
          >
            <WhatsappLogo size={18} weight="fill" className="group-hover:scale-110 transition-transform" />
            <span className="hidden sm:inline">{t.floating.whatsapp}</span>
            <span className="sm:hidden">WhatsApp</span>
          </a>

          <a
            data-testid="floating-call"
            href={`tel:${contacts.phoneTel}`}
            className="group inline-flex items-center gap-2 bg-[#8B7355] text-white pl-5 pr-6 py-3.5 text-sm shadow-xl hover:bg-[#1A1A1A] transition-colors"
          >
            <Phone size={18} weight="fill" className="group-hover:animate-pulse" />
            <span className="hidden sm:inline">{t.floating.call}</span>
            <span className="sm:hidden">{contacts.phoneDisplay}</span>
          </a>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
